// src/pages/UserPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Badge,
  Pagination, Row, Col
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ProtectedSection from '../components/ProtectedSection';
import userService from '../services/userService';
import UserModal from '../components/UserModal';

// en haut de UserPage.jsx (au-dessus du composant)
const normalizeRights = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        return Object.keys(parsed).filter(k => !!parsed[k]);
      }
      return [];
    } catch {
      return [];
    }
  }
  if (typeof val === 'object') {
    return Object.keys(val).filter(k => !!val[k]);
  }
  return [];
};



export default function UserPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);

  // Pagination (front)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  const fetchUsers = () => {
    userService.list()
      .then(setRows)
      .catch(err => {
        if (err.response?.status === 403) navigate('/login');
      });
  };
  useEffect(fetchUsers, []);

  // Filtre texte
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter(u =>
      u.username.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  // Découpage pour pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Revenir page 1 si search/pageSize changent
  useEffect(() => { setPage(1); }, [search, pageSize]);

  // Corriger si on dépasse le nombre de pages
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const end = Math.min(total, start + pageSize);
  const pageItems = filtered.slice(start, end);

  // Helpers affichage pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    // Fenêtre compacte (page courante +/- 2)
    const items = [];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);

    items.push(
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
    );

    if (from > 1) items.push(<Pagination.Ellipsis key="ell1" disabled />);

    for (let p = from; p <= to; p++) {
      items.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (to < totalPages) items.push(<Pagination.Ellipsis key="ell2" disabled />);

    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );

    return <Pagination className="m-0">{items}</Pagination>;
  };

  const openCreate = () => { setEdit(null); setShow(true); };
  const openEdit = (u) => { setEdit(u); setShow(true); };

  const remove = (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    userService.remove(id).then(fetchUsers);
  };

  const save = (payload) => {
    const op = edit?.id ? userService.update(edit.id, payload) : userService.create(payload);
    op.then(() => { setShow(false); fetchUsers(); });
  };

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Utilisateurs</h2>
        <ProtectedSection code="USER_CREATE">
          <Button onClick={openCreate}>+ Nouvel utilisateur</Button>
        </ProtectedSection>
      </div>

      <Row className="align-items-center mb-3">
        <Col md={8}>
          <InputGroup>
            <Form.Control
              placeholder="Rechercher (username)…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <Button onClick={() => setSearch('')}>×</Button>
          </InputGroup>
        </Col>
        <Col md={4} className="text-md-end mt-2 mt-md-0">
          <Form.Select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            style={{ width: 140, display: 'inline-block' }}
            aria-label="Taille de page"
          >
            {/*<option value={1}>1 / page</option>
            <option value={2}>2 / page</option>
            */}
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </Form.Select>
        </Col>
      </Row>

      <UserModal
        show={show}
        onHide={() => setShow(false)}
        onSave={save}
        initialData={edit}
      />

      <ProtectedSection code="USER_VIEW">
        <Table hover responsive>
          <thead>
            <tr>
              <th>Username</th>
              {/* <th>Email</th> */}
              <th>Droits</th>
              <th style={{ width: 210 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map(u => (
              <tr key={u.id}>
                <td>{u.username}</td>
                {/* <td>{u.email || '—'}</td> */}
                <td>
                  {normalizeRights(u.rights).map(code => (
                    <Badge key={code} bg="secondary" className="me-1">{code}</Badge>
                  ))}
                </td>
                <td>
                  <ProtectedSection code="USER_UPDATE">
                    <Button
                      size="sm" variant="outline-secondary"
                      className="me-2"
                      onClick={() => openEdit(u)}
                    >
                      Modifier
                    </Button>
                  </ProtectedSection>
                  <ProtectedSection code="USER_DELETE">
                    <Button
                      size="sm" variant="outline-danger"
                      onClick={() => remove(u.id)}
                    >
                      Supprimer
                    </Button>
                  </ProtectedSection>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {total === 0 && (
          <p className="text-center text-muted m-0">Aucun résultat.</p>
        )}

        {/* Footer pagination + infos */}
        {total > 0 && (
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Résultats : {start + 1}–{end} sur {total}
            </small>
            {renderPagination()}
          </div>
        )}
      </ProtectedSection>
    </Container>
  );
}
